import React, { memo, useEffect, useMemo, useState } from 'react';
import BaseModal from '../utils/BaseModal/BaseModal';
import { ALBUM_MEDIA_TYPE, UPLOAD_FILE_CONSTRAINTS } from '../../../utils/constants';
import UploadMediaButton from '../../UploadMediaButton/UploadMediaButton';
import ErrorContainer from '../../utils/ErrorContainer/ErrorContainer';
import { Container, Content, Form, StyledButton } from './UploadAlbumMediaModal.styles';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useFieldArray, useForm } from 'react-hook-form';
import { uploadAssistedPhoto, uploadAssistedVideo } from '../../../store/slices/user/asyncThunks';
import { notifyError, notifyInfo } from '../../../utils/notify';
import MediaItem from '../../MediaItem/MediaItem';
import SelectAlt from '../../utils/SelectAlt/SelectAlt';
import { selectFetchAssetFoldersInfo } from '../../../store/slices/assetFolder/slice';

const uploadMediaFormSchema = yup.object({
  media: yup.array().min(1, 'At least one media item are required'),
});

const UploadAlbumMediaModal = ({ modalRef, tag, onSuccess, influencerId }) => {
  const dispatch = useDispatch();
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadingProgress, setUploadingProgress] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const { data: folders } = useSelector(selectFetchAssetFoldersInfo);

  const folderOptions = useMemo(
    () => (folders || []).map(folder => ({ value: folder.id, label: folder.name })),
    [folders],
  );

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    delayError: 300,
    defaultValues: {
      media: [],
    },
    resolver: yupResolver(uploadMediaFormSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'media',
  });

  const myUploadProgress = index => progress => {
    let percentage = progress;
    setUploadingProgress(prev => {
      const newProgress = [...prev];
      newProgress[index] = percentage > 99 ? 99 : percentage.toFixed(1);
      return newProgress;
    });
  };

  useEffect(() => {
    if (uploadingProgress.length && !uploadingProgress.filter(progress => progress !== 100).length) {
      onSuccess();
      modalRef.current.hide();
      notifyInfo('Media uploaded successfully');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadingProgress]);

  const onPhotoDelete = (index, fileName) => {
    remove(index);
    setUploadErrors(prev => prev.filter(error => error.fileName !== fileName));
  };

  const onSubmit = async data => {
    setLoading(true);
    setUploadingProgress(data.media.map(_ => 0.01));
    const folderId = selectedFolder?.value;
    for (const [index, media] of data.media.entries()) {
      const isVideo = !!media.file.type.includes('video');
      if (isVideo) {
        try {
          await dispatch(
            uploadAssistedVideo({
              video: media,
              type: tag,
              onProgress: myUploadProgress(index),
              influencerId,
              ...(folderId && { folderId }),
            }),
          ).unwrap();
          setUploadingProgress(prev => {
            const newProgress = [...prev];
            newProgress[index] = 100;
            return newProgress;
          });
        } catch (error) {
          setUploadingProgress(prev => {
            const newProgress = [...prev];
            newProgress[index] = 0;
            return newProgress;
          });
          notifyError(error.message);
          setLoading(false);
        }
      } else {
        try {
          await dispatch(
            uploadAssistedPhoto({
              photo: media,
              type: tag,
              onProgress: myUploadProgress(index),
              influencerId,
              ...(folderId && { folderId }),
            }),
          ).unwrap();
          setUploadingProgress(prev => {
            const newProgress = [...prev];
            newProgress[index] = 100;
            return newProgress;
          });
        } catch (error) {
          setUploadingProgress(prev => {
            const newProgress = [...prev];
            newProgress[index] = 0;
            return newProgress;
          });
          notifyError(error.message);
          setLoading(false);
        }
      }
    }
  };

  return (
    <BaseModal
      title="Upload Media"
      ref={modalRef}
      onClose={() => {
        modalRef.current.hide();
      }}
      onAfterClose={() => {
        reset();
        setLoading(false);
        setUploadErrors([]);
        setUploadingProgress([]);
        setSelectedFolder(null);
      }}>
      <Container>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Content>
            {fields.map((field, index) => {
              const mediaType = field.file.type.includes('video') ? ALBUM_MEDIA_TYPE.VIDEO : ALBUM_MEDIA_TYPE.PHOTO;
              return (
                <MediaItem
                  key={index}
                  kind={mediaType}
                  type={mediaType}
                  file={field}
                  progress={uploadingProgress[index]}
                  onDelete={() => onPhotoDelete(index, field.file.name)}
                  uploadError={uploadErrors.find(error => error.fileName === field.file.name)}
                />
              );
            })}
          </Content>
          <UploadMediaButton
            disabled={loading}
            type={UPLOAD_FILE_CONSTRAINTS.TYPE.MIXED}
            onChange={files => {
              for (const file of files) {
                append(file);
              }
            }}
            setError={setUploadErrors}
            multiple={true}
          />
          <ErrorContainer errorText={errors.media?.message || errors.media?.root?.message} />
          <SelectAlt
            label="Folder (optional)"
            placeholder="Select a folder"
            options={folderOptions}
            selectedOption={selectedFolder}
            handleChange={setSelectedFolder}
            disabled={loading}
            isClearable
            isSearchable
          />
          <StyledButton title="Save" isLoading={loading} disabled={uploadErrors.length} />
        </Form>
      </Container>
    </BaseModal>
  );
};

export default memo(UploadAlbumMediaModal);
